// Repeat visit / product stalking detection — Amazon pattern
import { useEffect, useMemo } from "react";
import { trackEvent } from "@/lib/analytics";

const STORAGE_KEY = "djayma-product-visits";
const MAX_ENTRIES = 200;

interface VisitRecord {
  readonly count: number;
  readonly first_seen: string;
  readonly last_seen: string;
  readonly price_first: number;
}

function loadVisits(): Record<string, VisitRecord> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* fresh */ }
  return {};
}

function saveVisits(visits: Record<string, VisitRecord>): void {
  try {
    // LRU eviction: keep most recently visited
    const entries = Object.entries(visits);
    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) =>
        new Date(b[1].last_seen).getTime() - new Date(a[1].last_seen).getTime()
      );
      visits = Object.fromEntries(entries.slice(0, MAX_ENTRIES));
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visits));
  } catch { /* storage full */ }
}

export function useRepeatVisitTracking(
  productId: string | null,
  currentPriceFcfa: number = 0,
) {
  const visits = useMemo(() => loadVisits(), []);

  useEffect(() => {
    if (!productId) return;

    const existing = visits[productId];
    const now = new Date().toISOString();

    if (existing) {
      const updated: VisitRecord = {
        ...existing,
        count: existing.count + 1,
        last_seen: now,
      };

      const daysBetween = Math.round(
        (Date.now() - new Date(existing.last_seen).getTime()) / 86_400_000
      );

      if (updated.count >= 5) {
        trackEvent("product_stalked", {
          product_id: productId,
          visit_count: updated.count,
          price_fcfa: currentPriceFcfa,
        });
      } else if (updated.count >= 2) {
        trackEvent("product_revisited", {
          product_id: productId,
          visit_count: updated.count,
          first_visit_at: existing.first_seen,
          last_visit_at: now,
          days_between_visits: daysBetween,
          price_at_first_visit_fcfa: existing.price_first,
          current_price_fcfa: currentPriceFcfa,
          price_changed: currentPriceFcfa !== existing.price_first,
        });
      }

      visits[productId] = updated;
    } else {
      visits[productId] = {
        count: 1,
        first_seen: now,
        last_seen: now,
        price_first: currentPriceFcfa,
      };
    }

    saveVisits(visits);
  }, [productId, currentPriceFcfa, visits]);

  const record = productId ? visits[productId] : undefined;

  return {
    visitCount: record?.count ?? 0,
    firstVisitAt: record?.first_seen ?? null,
    isRepeatVisitor: (record?.count ?? 0) >= 2,
    daysSinceFirstVisit: record
      ? Math.round((Date.now() - new Date(record.first_seen).getTime()) / 86_400_000)
      : 0,
  };
}

// ── Return Visitor Detection ────────────────────────────────────────────────

export function useReturnVisitor() {
  useEffect(() => {
    const visits = loadVisits();
    const viewedProducts = Object.keys(visits);
    const totalEntries = viewedProducts.length;

    if (totalEntries === 0) return;

    const lastVisitDate = Object.values(visits)
      .map((v) => new Date(v.last_seen).getTime())
      .sort((a, b) => b - a)[0];

    const daysSince = Math.round((Date.now() - lastVisitDate) / 86_400_000);

    if (daysSince >= 1) {
      const visitCounts = Object.values(visits).map((v) => v.count);
      const avgVisits = visitCounts.reduce((a, b) => a + b, 0) / visitCounts.length;

      let visitorType: string = "returning";
      if (avgVisits > 3 && totalEntries > 10) visitorType = "window_shopper";
      else if (totalEntries > 5) visitorType = "comparison_shopper";

      trackEvent("visitor_returned", {
        total_visits: totalEntries,
        days_since_last_visit: daysSince,
        previously_viewed_products: viewedProducts.slice(0, 10),
        visitor_type: visitorType,
      });
    }
  }, []);
}
